import { Alert, Button, CircularProgress, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { authService } from "@/services/AuthService";
import type { ReactNode } from "react";
import { useAuthenticatedUser } from "@/contexts/AuthenticatedUserContext";
import { canAccessGroupSummary, hasSalePermission, type SalePermission } from "@/lib/sale-permissions";

export function useSalePermissions() {
  const auth = useAuthenticatedUser();
  const ready = !auth.loading && !auth.error;
  return {
    ...auth,
    can: (permission: SalePermission) => ready && hasSalePermission(auth.session || null, permission),
    canSummary: (pathname: string) => ready && canAccessGroupSummary(auth.session || null, pathname),
  };
}

export default function SalePermissionBoundary({ required, children, allowSummary = false, anyOf }: {
  required: SalePermission[]; children: ReactNode; allowSummary?: boolean; anyOf?: SalePermission[];
}) {
  const auth = useSalePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  if (auth.loading) return <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress aria-label={t("permissionsLoading")} /></Stack>;
  if ((allowSummary && auth.canSummary(location.pathname)) || (required.every(auth.can) && (!anyOf || anyOf.some(auth.can)))) return children;
  return <Alert severity="warning" action={<Button onClick={() => {
    void authService.logout();
    navigate("/sale-support/auth", { replace: true });
  }}>{t("signInAgain")}</Button>}>
    {t(auth.error ? "permissionsUnavailable" : "permissionPageDenied")}
  </Alert>;
}
